import React, { useMemo, useState } from 'react';
import { PrepComment } from '../../../../types/meeting';
import TrashIcon from '../../../icons/TrashIcon';
import EditIcon from '../../../icons/EditIcon';

const MAX_BODY = 4000; // PrepComment.MaxBodyLength

interface PrepCommentRailProps {
  comments: PrepComment[];
  /** Page the reader is on. New notes anchor here. */
  page: number;
  /** Set for a material with no inline rendering: notes carry no page anchor. */
  isDocumentLevel?: boolean;
  busy?: boolean;
  error?: string | null;
  onCreate: (body: string) => Promise<void>;
  onUpdate: (comment: PrepComment, body: string) => Promise<void>;
  onDelete: (comment: PrepComment) => Promise<void>;
  onJumpToPage: (page: number) => void;
}

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

/**
 * The reading surface's notes rail: every note the signed-in user has left on
 * this material, grouped by page, with the composer for the current page.
 *
 * These notes are private to their author, so the rail shows no author name, no
 * sharing control, and no ownership-conditional affordances — everything listed
 * here belongs to the person reading it.
 */
const PrepCommentRail: React.FC<PrepCommentRailProps> = ({
  comments,
  page,
  isDocumentLevel,
  busy,
  error,
  onCreate,
  onUpdate,
  onDelete,
  onJumpToPage,
}) => {
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [saving, setSaving] = useState(false);

  // Grouped by page so a note is always read next to where it was written.
  // Document-level notes (a material that cannot be paged) sort first under
  // their own heading rather than being folded into page 1.
  const groups = useMemo(() => {
    const byPage = new Map<number | null, PrepComment[]>();
    for (const c of comments) {
      const key = c.is_document_level ? null : c.page_number ?? null;
      const bucket = byPage.get(key);
      if (bucket) bucket.push(c);
      else byPage.set(key, [c]);
    }
    return Array.from(byPage.entries()).sort(([a], [b]) => {
      if (a === null) return -1;
      if (b === null) return 1;
      return a - b;
    });
  }, [comments]);

  const submit = async () => {
    const body = draft.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      await onCreate(body);
      setDraft('');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (comment: PrepComment) => {
    const body = editDraft.trim();
    if (!body || saving) return;
    setSaving(true);
    try {
      await onUpdate(comment, body);
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className="prep-rail" aria-label="Your prep notes">
      <div className="prep-rail-header">
        <h4 className="prep-rail-title">Your prep notes</h4>
        <p className="prep-rail-subtitle">
          Private to you. Never used as AI context.
        </p>
      </div>

      {error && <div className="prep-rail-error">{error}</div>}

      <div className="prep-rail-composer">
        <label className="sr-only" htmlFor="prep-rail-draft">
          {isDocumentLevel ? 'New note on this document' : `New note on page ${page}`}
        </label>
        <textarea
          id="prep-rail-draft"
          className="prep-rail-textarea"
          rows={3}
          maxLength={MAX_BODY}
          value={draft}
          placeholder={
            isDocumentLevel
              ? 'Add a note on this document…'
              : `Add a note on page ${page}…`
          }
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="prep-rail-composer-actions">
          <span className="prep-rail-anchor-hint">
            {isDocumentLevel ? 'This document' : `Page ${page}`}
          </span>
          <button
            className="meeting-btn meeting-btn-primary meeting-btn-sm"
            disabled={!draft.trim() || saving}
            onClick={submit}
          >
            {saving ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </div>

      {busy && comments.length === 0 ? (
        <div className="meeting-empty-hint">Loading your notes…</div>
      ) : comments.length === 0 ? (
        <div className="meeting-empty-hint">
          No notes on this material yet. Notes pin to the page you are reading.
        </div>
      ) : (
        <div className="prep-rail-list">
          {groups.map(([groupPage, items]) => (
            <section className="prep-rail-group" key={groupPage ?? 'document'}>
              {groupPage === null ? (
                <div className="prep-rail-group-heading">This document</div>
              ) : (
                <button
                  className={`prep-rail-group-heading prep-rail-group-jump ${groupPage === page ? 'current' : ''}`}
                  onClick={() => onJumpToPage(groupPage)}
                  aria-label={`Go to page ${groupPage}`}
                >
                  Page {groupPage}
                </button>
              )}

              {items.map((c) => (
                <article className="prep-rail-item" key={c.id}>
                  {editingId === c.id ? (
                    <>
                      <textarea
                        className="prep-rail-textarea"
                        rows={3}
                        maxLength={MAX_BODY}
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        aria-label="Edit note"
                      />
                      <div className="prep-rail-item-actions">
                        <button
                          className="meeting-btn meeting-btn-secondary meeting-btn-sm"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="meeting-btn meeting-btn-primary meeting-btn-sm"
                          disabled={!editDraft.trim() || saving}
                          onClick={() => saveEdit(c)}
                        >
                          Save
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="prep-rail-body">{c.body}</p>
                      <div className="prep-rail-item-footer">
                        <span className="prep-rail-when">
                          {formatWhen(c.created_at)}
                          {c.updated_at ? ' · edited' : ''}
                        </span>
                        <span className="prep-rail-item-actions">
                          <button
                            className="meeting-file-action-btn"
                            title="Edit note"
                            aria-label="Edit note"
                            onClick={() => {
                              setEditingId(c.id);
                              setEditDraft(c.body);
                            }}
                          >
                            <EditIcon size={16} />
                          </button>
                          <button
                            className="meeting-file-action-btn meeting-file-action-danger"
                            title="Delete note"
                            aria-label="Delete note"
                            onClick={() => onDelete(c)}
                          >
                            <TrashIcon size={16} />
                          </button>
                        </span>
                      </div>
                    </>
                  )}
                </article>
              ))}
            </section>
          ))}
        </div>
      )}
    </aside>
  );
};

export default PrepCommentRail;
